import EventEmitter from "EventEmitter";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { HDRCubeTextureLoader } from "three/examples/jsm/loaders/HDRCubeTextureLoader";

import { TweenLite, Power1 } from "gsap";

// Path for the environment GLB

import environment from "../../assets/environment/envLowPoly.glb";

import ambient_nx from "../../assets/textures/hdr/nx.hdr";
import ambient_ny from "../../assets/textures/hdr/ny.hdr";
import ambient_nz from "../../assets/textures/hdr/nz.hdr";
import ambient_px from "../../assets/textures/hdr/px.hdr";
import ambient_py from "../../assets/textures/hdr/py.hdr";
import ambient_pz from "../../assets/textures/hdr/pz.hdr";

import shadowTexture from "../../assets/textures/roundshadow.png";

export default class Environment extends EventEmitter {
  get HDRI() {
    return {
      STUDIO: this.studioHDR,
      AMBIENT: this.ambientHDR,
    };
  }

  get BackgroundEnabled() {
    return this._backgroundEnabled;
  }

  set BackgroundEnabled(value) {
    this._backgroundEnabled = value;
  }

  constructor(scene, renderer) {
    super();

    this.renderer = renderer;
    this.scene = scene;
    this.ground = {};

    this.ambientHDR = [
      ambient_px,
      ambient_nx,
      ambient_py,
      ambient_ny,
      ambient_pz,
      ambient_nz,
    ];



    this.createObjects();
    this.createLights();
    this.loadHdri(this.ambientHDR);
  }

  /**
   * Load all neccessary models for the environment
   */
  createObjects = () => {


    //save the array of meshes to update the shadows later
    this.meshes = []
    const tile = 50;
    const loader = new GLTFLoader();

    loader.load(environment,(gltf) => {

      this.mainScene = gltf.scene;
      gltf.scene.position.y = 0.023;
      gltf.scene.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) {
          return;
        }       

         switch(child.material.name){
         case 'dome':
  
            this.domeMaterial = new THREE.MeshBasicMaterial({
              map:child.material.map,
              transparent: true,
              depthWrite: true,
              opacity: 0,
            });
    
            child.material = this.domeMaterial;
            this.domeMaterial.transparent = true;
            
            break;
          case 'envLowPoly':
            this.envMaterial = child.material;
            this.envMaterial.transparent = true;
            this.envMaterial.opacity = 0;

            break;
            case 'floor':
              this.groundMaterial = child.material;
              this.groundMaterial.transparent = true;
              this.groundMaterial.opacity = 0;
              
              this.groundMaterial.map.wrapS = THREE.RepeatWrapping;
              this.groundMaterial.map.wrapT = THREE.RepeatWrapping;
              this.groundMaterial.map.repeat = new THREE.Vector2(tile,tile);

              this.groundMaterial.aoMap.wrapS = THREE.RepeatWrapping;
              this.groundMaterial.aoMap.wrapT = THREE.RepeatWrapping;
              this.groundMaterial.aoMap.repeat = new THREE.Vector2(tile,tile);

              this.groundMaterial.normalMap.wrapS = THREE.RepeatWrapping;
              this.groundMaterial.normalMap.wrapT = THREE.RepeatWrapping;
              this.groundMaterial.normalMap.repeat = new THREE.Vector2(tile,tile);

              this.groundMaterial.roughnessMap.wrapS = THREE.RepeatWrapping;
              this.groundMaterial.roughnessMap.wrapT = THREE.RepeatWrapping;
              this.groundMaterial.roughnessMap.repeat = new THREE.Vector2(tile,tile);

              //Assign a local reference of the ground so we can change shadows
              this.ground = child;
              this.ground.receiveShadow = this.UseShadows;
              
            break;
          default:

          break;
        }
        this.toggleBackground(0,0 );
      });

      
      gltf.scene.scale.set(0.01, 0.01, 0.01);
      
      this.scene.add(gltf.scene);

    });


    this.createShadow();
    
    
  };


    /**
   * Create contact shadow
   */
  createShadow = () => {
    const loader = new THREE.TextureLoader();
    const texture = loader.load(shadowTexture);

    this.shadowMat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true, // so we can see the ground
      depthWrite: true, // so we don't have to sort
    });

    const planeGeometry = new THREE.PlaneBufferGeometry(1.5, 1.52, 1, 1);
    planeGeometry.rotateX(-Math.PI / 2);

    const plane = new THREE.Mesh(planeGeometry, this.shadowMat);
    plane.position.set(0, 0.024, 0.15);
    plane.renderOrder = 3;

    this.scene.add(plane);
  };


  /**
   * Create Lights
   * @todo Light has to be done with auto team
   */
  createLights = () => {
    this.directionalLight = new THREE.DirectionalLight(0xfffdf1, 4 , 10);
    this.directionalLight.position.set(-0.7, 1, -0.5);
    this.scene.add(this.directionalLight);

    if(this.UseShadows)
      this.directionalLight.castShadow = true;    
    
    const res = 256;
    this.directionalLight.shadow.mapSize.width = res;  // default
    this.directionalLight.shadow.mapSize.height = res; // default
    this.directionalLight.shadow.camera.near = 0.1;    // default
    this.directionalLight.shadow.camera.far = 100;     // default
    
   
    const directionalLight2 = new THREE.DirectionalLight(0xfaf5d4, 3.0 , 10);
    directionalLight2.position.set(2.5, 0.5, 2);
    this.scene.add(directionalLight2);

    const directionalLight3 = new THREE.DirectionalLight(0xfaf5d4, 2.0 , 10);
    directionalLight3.position.set(-2.5, 0.5, -1);
    this.scene.add(directionalLight3);


    const pointLight = new THREE.PointLight(0xfaf5d4, 2.0 , 10);
    pointLight.position.set(1.5, 0, -1);
    this.scene.add(pointLight);

  };

  get UseShadows() {
    return this._useShadows;
  }

  set UseShadows(useShadows) {

    this.shadowMat.opacity = !useShadows ? 1 : 0;
    this.shadowMat.needsUpdate = true;
    this.ground.receiveShadow = useShadows;
    this.directionalLight.castShadow = useShadows;    
    this._useShadows = useShadows;
  }

  /**
   * Load HDR
   * @param {array} images - Array of Images
   */
  loadHdri = (images) => {

    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    pmremGenerator.compileCubemapShader();

    this.hdrCubeMap = new HDRCubeTextureLoader()
      .setDataType(THREE.UnsignedByteType)
      .load(images, () => {
        // console.log('images');
        const hdrCubeRenderTarget = pmremGenerator.fromCubemap(this.hdrCubeMap);


        this.hdrCubeMap.magFilter = THREE.LinearFilter;
        this.hdrCubeMap.needsUpdate = true;

        if(this.groundMaterial){
          this.groundMaterial.envMap = this.hdrCubeMap;
          this.groundMaterial.needsUpdate = true;
        }

        this.toggleBackground(0,0);
        this.emit("onHDRLoaded", hdrCubeRenderTarget.texture);
      });
  };

  /**
   * Load Model
   * @todo Should we abstract a generic function in a Utils class to load 3D models?
   */
  loadModel = (path) => {
    const loader = new GLTFLoader();
    loader.load(
      path,
      (gltf) => {
        //this.scene.add( gltf.scene )s;
      },
      undefined,
      (error) => {
        console.error("error: ", error);
      }
    );
  };

  /**
   * Show or hide background
   * @param {bool} value - Show or hide background
   * @param {float} time - transition time
   */
  toggleBackground = (value, time = 0.2) => {

    if(!this.envMaterial || !this.groundMaterial)return;

    this.BackgroundEnabled = value;
    const alphaTo = {
      alpha: value ? 0 : 1,
    };
    TweenLite.to(alphaTo, time, {
      alpha: value ? 1 : 0,
      ease: Power1.easeOut,
      onUpdate: () => {
        this.envMaterial.opacity = alphaTo.alpha;
        this.envMaterial.needsUpdate = true;
        this.groundMaterial.opacity = alphaTo.alpha;
        this.groundMaterial.needsUpdate = true;
        this.domeMaterial.opacity = alphaTo.alpha;
        this.domeMaterial.needsUpdate = true;

        //If we don't have realtime shadows enabled we always use the contact shadow
        this.shadowMat.opacity = !this.UseShadows ? 1  : 1 - alphaTo.alpha;
        this.shadowMat.needsUpdate = true;

        if(alphaTo > 0 && !this.mainScene.visible)
          this.mainScene.visible = true;
        else if(alphaTo <= 0 && this.mainScene.visible)
          this.mainScene.visible = false;


      },
    });
  };
}
